import './coapplicantPreviousResidencePage.scss';

import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import red from '@mui/material/colors/red';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import TextField from '@mui/material/TextField';
import { isEmpty, toString } from 'lodash';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { NavigationPane } from '../../../components/shared_new/NavigationPane/NavigationPane';
import { USStates } from '../../../constants/states';
import useCoapplicantStore from '../../../store/coapplicantStore/useCoapplicantStore';
import useNavigationPaneStore from '../../../store/navigationPaneStore/useNavigationPaneStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';
import { inputEventHandler } from '../../../utils/component/inputEventHandler';

export function Component(): JSX.Element {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { next, previous } = useWorkflowStore();
  const { setDrawerOpen } = useNavigationPaneStore();

  const {
    coapplicantPreviousResidenceCity,
    coapplicantPreviousResidenceLineOne,
    coapplicantPreviousResidenceLineTwo,
    coapplicantPreviousResidencePostalCode,
    coapplicantPreviousResidenceState,
    isSyncingCoapplicant,
    syncingCoapplicantFailedMessage,
    setCoapplicantPreviousResidenceCity,
    setCoapplicantPreviousResidenceLineOne,
    setCoapplicantPreviousResidenceLineTwo,
    setCoapplicantPreviousResidencePostalCode,
    setCoapplicantPreviousResidenceState,
    syncCoapplicant,
  } = useCoapplicantStore();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isDirty, isValid },
  } = useForm({
    mode: 'onBlur',
    criteriaMode: 'firstError',
    delayError: 200,
  });

  const previousStep = (): void => {
    navigate(previous());
  };

  const addressValidation = {
    required: t('validation.address.required'),
  };

  const cityValidation = {
    required: t('validation.city.required'),
  };

  const stateValidation = {
    required: t('validation.state.required'),
  };

  const postalCodeValidation = {
    required: t('validation.zipCode.required'),
    minLength: {
      value: 5,
      message: t('validation.zipCode.minMax', { minMax: 5 }),
    },
    maxLength: {
      value: 5,
      message: t('validation.zipCode.minMax', { minMax: 5 }),
    },
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const {
      cityValue,
      lineOneValue,
      lineTwoValue,
      stateValue,
      postalCodeValue,
    } = values;

    if (!cityValue || !lineOneValue || !stateValue || !postalCodeValue) {
      return;
    }

    setCoapplicantPreviousResidenceCity(cityValue);
    setCoapplicantPreviousResidenceLineOne(lineOneValue);
    setCoapplicantPreviousResidenceLineTwo(lineTwoValue);
    setCoapplicantPreviousResidencePostalCode(postalCodeValue);
    setCoapplicantPreviousResidenceState(stateValue);

    if (isDirty) {
      const synced = await syncCoapplicant();
      if (synced) {
        navigate(next());
      }

      return;
    }

    navigate(next());
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        className="coapplicant-previous-residence-page"
        sx={theme.pageLayout}
      >
        <header
          data-testid={locator.pages.coapplicantPreviousResidence.header.root}
        >
          <button
            aria-label="Previous step"
            data-testid={
              locator.pages.coapplicantPreviousResidence.header
                .previousStepButton
            }
            type="button"
            onClick={previousStep}
          >
            <KeyboardBackspaceRoundedIcon />
          </button>
          <button
            aria-label="Toggle drawer"
            data-testid={
              locator.pages.coapplicantPreviousResidence.header.drawerButton
            }
            type="button"
            onClick={() => {
              setDrawerOpen(true);
            }}
          >
            <MenuRoundedIcon />
          </button>
        </header>

        <div className="progress-container" role="complementary">
          <LinearProgress
            aria-label="Application completed percentage"
            data-testid={
              locator.pages.coapplicantPreviousResidence.progress.root
            }
            value={useWorkflowStore.getState().computed.progress}
            variant="determinate"
          />
        </div>

        <main>
          <form
            data-testid={locator.pages.coapplicantPreviousResidence.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1
                data-testid={locator.pages.coapplicantPreviousResidence.form.h1}
              >
                {t('CoapplicantPreviousResidencePage.title')}
              </h1>
              <h2
                data-testid={locator.pages.coapplicantPreviousResidence.form.h2}
              >
                {t('CoapplicantPreviousResidencePage.subtitle')}
              </h2>

              {!isEmpty(syncingCoapplicantFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.coapplicantPreviousResidence.alert.error
                    }
                    icon={false}
                    severity="error"
                  >
                    {syncingCoapplicantFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div style={{ marginTop: '1.5rem' }}>
                  <TextField
                    {...register('lineOneValue', addressValidation)}
                    autoComplete="address-line1"
                    defaultValue={coapplicantPreviousResidenceLineOne}
                    error={!isEmpty(errors.lineOneValue)}
                    inputProps={{
                      'data-testid':
                        locator.pages.coapplicantPreviousResidence.form
                          .addressLine1Input,
                    }}
                    label={t(
                      'CoapplicantPreviousResidencePage.residenceLineOne',
                    )}
                    variant="outlined"
                    fullWidth
                  />

                  {!isEmpty(errors.lineOneValue) && (
                    <span
                      data-testid={
                        locator.pages.coapplicantPreviousResidence.form
                          .addressLine1InputError
                      }
                      style={{ fontSize: '12px', color: red[700] }}
                    >
                      {toString(errors.lineOneValue.message)}
                    </span>
                  )}
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <div className="inline-inputs">
                    <div className="input-half">
                      <TextField
                        {...register('lineTwoValue')}
                        autoComplete="address-line2"
                        defaultValue={coapplicantPreviousResidenceLineTwo}
                        inputProps={{
                          'data-testid':
                            locator.pages.coapplicantPreviousResidence.form
                              .addressLine2Input,
                        }}
                        label={t(
                          'CoapplicantPreviousResidencePage.residenceLineTwo',
                        )}
                        variant="outlined"
                        fullWidth
                      />
                    </div>

                    <div className="input-half">
                      <TextField
                        {...register('cityValue', cityValidation)}
                        autoComplete="address-level2"
                        defaultValue={coapplicantPreviousResidenceCity}
                        error={!isEmpty(errors.cityValue)}
                        inputProps={{
                          'data-testid':
                            locator.pages.coapplicantPreviousResidence.form
                              .cityInput,
                        }}
                        label={t(
                          'CoapplicantPreviousResidencePage.residenceCity',
                        )}
                        variant="outlined"
                        fullWidth
                      />

                      {!isEmpty(errors.cityValue) && (
                        <span
                          data-testid={
                            locator.pages.coapplicantPreviousResidence.form
                              .cityInputError
                          }
                          style={{ fontSize: '12px', color: red[700] }}
                        >
                          {toString(errors.cityValue.message)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <div className="inline-inputs">
                    <div className="input-half">
                      <TextField
                        {...register('stateValue', stateValidation)}
                        SelectProps={{
                          IconComponent: ExpandMoreRoundedIcon,
                        }}
                        autoComplete="address-level1"
                        error={!isEmpty(errors.stateValue)}
                        inputProps={{
                          'data-testid':
                            locator.pages.coapplicantPreviousResidence.form
                              .stateSelect,
                        }}
                        label={t(
                          'CoapplicantPreviousResidencePage.residenceState',
                        )}
                        value={watch(
                          'stateValue',
                          coapplicantPreviousResidenceState || '',
                        )}
                        variant="outlined"
                        fullWidth
                        select
                      >
                        <MenuItem value="" disabled>
                          {t('CoapplicantPreviousResidencePage.residenceState')}
                        </MenuItem>

                        {USStates.map((stateObj) => {
                          return (
                            <MenuItem
                              key={stateObj.abbreviation}
                              value={stateObj.abbreviation}
                            >
                              {stateObj.name}
                            </MenuItem>
                          );
                        })}
                      </TextField>

                      {!isEmpty(errors.stateValue) && (
                        <span
                          data-testid={
                            locator.pages.coapplicantPreviousResidence.form
                              .stateSelectError
                          }
                          style={{ fontSize: '12px', color: red[700] }}
                        >
                          {toString(errors.stateValue.message)}
                        </span>
                      )}
                    </div>

                    <div className="input-half">
                      <TextField
                        {...register('postalCodeValue', postalCodeValidation)}
                        autoComplete="postal-code"
                        defaultValue={coapplicantPreviousResidencePostalCode}
                        error={!isEmpty(errors.postalCodeValue)}
                        inputProps={{
                          'data-testid':
                            locator.pages.coapplicantPreviousResidence.form
                              .zipCodeInput,
                          inputMode: 'numeric',
                        }}
                        label={t('CoapplicantPreviousResidencePage.zip')}
                        type="number"
                        variant="outlined"
                        fullWidth
                        onKeyDown={inputEventHandler.number.keydown}
                        onPasteCapture={inputEventHandler.number.pasteCapture}
                      />

                      {!isEmpty(errors.postalCodeValue) && (
                        <span
                          data-testid={
                            locator.pages.coapplicantPreviousResidence.form
                              .zipCodeInputError
                          }
                          style={{ fontSize: '12px', color: red[700] }}
                        >
                          {toString(errors.postalCodeValue.message)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: '3rem' }}>
                <LoadingButton
                  color="primary"
                  data-testid={
                    locator.pages.coapplicantPreviousResidence.form.submitButton
                  }
                  disabled={!isValid}
                  loading={isSyncingCoapplicant}
                  startIcon={<></>}
                  type="submit"
                  variant="contained"
                  fullWidth
                >
                  {t('CoapplicantPreviousResidencePage.continue')}
                </LoadingButton>
              </div>
            </div>
            <footer
              data-testid={
                locator.pages.coapplicantPreviousResidence.footer.root
              }
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'CoapplicantPreviousResidencePage';
